function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

if (!customElements.get("cart-drawer-disclosure")) {
  class CartDrawerDisclosure extends DetailsDisclosure {
    constructor() {
      super();
      this.openBtn = this.querySelector("summary");
      this.closeBtn = this.querySelector(".js-close");
      const drawer = document.getElementById("cart-drawer");
      this.overlay = drawer ? drawer.querySelector(".overlay") : null;
    }

    handleClick(evt) {
      if (evt.target.matches(".js-close")) this.close();
    }

    open() {
      if (this.overlay) this.overlay.classList.add("is-visible");
      super.open();
      trapFocus(this);
      this.clickHandler = this.clickHandler || this.handleClick.bind(this);
      this.keyupHandler = (evt) => evt.key === "Escape" && this.close();
      this.addEventListener("click", this.clickHandler);
      this.addEventListener("keyup", this.keyupHandler);
      if (this.overlay) this.overlay.addEventListener("click", this.clickHandler);
    }

    close() {
      if (this.overlay) this.overlay.classList.remove("is-visible");
      super.close();
      removeTrapFocus(this.openBtn);
      this.removeEventListener("click", this.clickHandler);
      this.removeEventListener("keyup", this.keyupHandler);
      if (this.overlay) this.overlay.removeEventListener("click", this.clickHandler);
    }
  }

  customElements.define("cart-drawer-disclosure", CartDrawerDisclosure);
}

if (!customElements.get("cart-drawer")) {
  class CartDrawer extends SideDrawer {
    constructor() {
      super();
      this.cartSummary = this.querySelector(".cart-drawer__summary");
      this.init();
      this.bindEvents();
    }

    disconnectedCallback() {
      document.removeEventListener("dispatch:cart-drawer:refresh", this.refreshHandler);
      document.removeEventListener("on:bfcache:load-restore", this.refreshHandler);
      document.removeEventListener("dispatch:cart-drawer:open", this.openDrawerViaEventHandler);
      document.removeEventListener("dispatch:cart-drawer:close", this.closeDrawerViaEventHandler);
      if (Shopify.designMode) {
        document.removeEventListener("shopify:section:select", this.sectionSelectHandler);
        document.removeEventListener("shopify:section:deselect", this.sectionDeselectHandler);
      }
      if (this.dcbLoadedHandler) {
        document.removeEventListener("shopify:payment_button:loaded", this.dcbLoadedHandler);
      }
    }

    init() {
      const cartIcon = document.getElementById("cart-icon");
      if (!cartIcon) return;
      cartIcon.setAttribute("role", "button");
      cartIcon.setAttribute("aria-haspopup", "dialog");
      const openFromIcon = (evt) => {
        evt.preventDefault();
        // open() always re-fetches cart sections before showing.
        this.open(cartIcon);
      };
      cartIcon.addEventListener("click", openFromIcon);
      cartIcon.addEventListener("keydown", (evt) => {
        if (evt.key === " ") openFromIcon(evt);
      });
    }

    bindEvents() {
      if (Shopify.designMode) {
        this.sectionSelectHandler = this.handleSectionSelect.bind(this);
        this.sectionDeselectHandler = this.handleSectionDeselect.bind(this);
        document.addEventListener("shopify:section:select", this.sectionSelectHandler);
        document.addEventListener("shopify:section:deselect", this.sectionDeselectHandler);
      }
      this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
      this.closeDrawerViaEventHandler = this.close.bind(this, null);
      document.addEventListener("dispatch:cart-drawer:open", this.openDrawerViaEventHandler);
      document.addEventListener("dispatch:cart-drawer:close", this.closeDrawerViaEventHandler);
      if (this.cartSummary && this.cartSummary.classList.contains("cart-drawer--checkout--sticky-true")) {
        this.dcbLoadedHandler = this.dcbLoadedHandler || CartDrawer.recalculateCssVarHeights;
        document.addEventListener("shopify:payment_button:loaded", this.dcbLoadedHandler);
      }
      this.refreshHandler = this.refresh.bind(this);
      document.addEventListener("dispatch:cart-drawer:refresh", this.refreshHandler);
      document.addEventListener("on:bfcache:load-restore", this.refreshHandler);
    }

    handleSectionSelect(evt) {
      if (evt.target === this.closest(".shopify-section")) this.open();
    }

    handleSectionDeselect(evt) {
      if (evt.target === this.closest(".shopify-section")) this.close();
    }

    handleDrawerOpenViaEvent(evt) {
      this.open(evt.detail ? evt.detail.opener : null);
    }

    isStickySummary() {
      const summary = this.cartSummary && this.cartSummary.isConnected
        ? this.cartSummary
        : this.querySelector(".cart-drawer__summary");
      this.cartSummary = summary;
      return !!(summary && summary.classList.contains("cart-drawer--checkout--sticky-true"));
    }

    async open(opener, elementToFocus) {
      // Always sync from the live cart before showing — fixes stale "A kosarad üres"
      // after ATC when the add.js section payload was missing/ignored.
      if (!this._openingSync) {
        this._openingSync = true;
        try {
          await this.syncFromCart();
        } catch (e) {
          console.log(e);
        }
        this._openingSync = false;
      }

      const quickAddDrawer = document.querySelector('quick-add-drawer[aria-hidden="false"]');
      const overlay = document.querySelector(".js-overlay.is-visible");
      if (quickAddDrawer) {
        if (overlay) overlay.style.transitionDelay = "200ms";
        quickAddDrawer.close();
      }
      setTimeout(() => {
        super.open(opener, elementToFocus, () => {
          if (this.isStickySummary()) CartDrawer.recalculateCssVarHeights();
        });
        if (this.isStickySummary()) CartDrawer.recalculateCssVarHeights();
        if (overlay) overlay.style.transitionDelay = "";
      }, quickAddDrawer ? 200 : 0);
      if (window.initLazyImages) window.initLazyImages();
    }

    async syncFromCart() {
      const response = this.getSectionsToRender().map((section) => section.section);
      const sections = await (await fetch(`?sections=${response.join(",")}`)).json();
      await this.renderContents({ sections }, false);

      // If UI still looks empty but cart.js has lines, retry once (cookie/consent race).
      if (this.querySelector(".js-cart-empty")) {
        const cart = await (await fetch(`${theme.routes.cart}.js`)).json();
        if (cart.item_count > 0) {
          const retry = await (await fetch(`?sections=${response.join(",")}`)).json();
          await this.renderContents({ sections: retry }, false);
        }
      }
    }

    async refresh(dontRefreshCartItems) {
      try {
        const cartItems = this.querySelector("cart-items");
        if (cartItems && !dontRefreshCartItems) {
          cartItems.refresh();
        } else {
          await this.syncFromCart();
        }
      } catch (error) {
        console.log(error);
        this.dispatchEvent(
          new CustomEvent("on:cart:error", {
            bubbles: true,
            detail: { error: this.errorMsg && this.errorMsg.textContent }
          })
        );
      }
    }

    async renderContents(data, openDrawer = true) {
      try {
        if (!this.scriptsLoaded) {
          if (!document.querySelector(`script[src="${theme.scripts.cartItems}"]`)) {
            await loadScript(theme.scripts.cartItems);
          }
          if (
            !document.querySelector(`script[src="${theme.scripts.shippingCalculator}"]`) &&
            this.dataset.shippingCalculator === "true"
          ) {
            await loadScript(theme.scripts.countryProvinceSelector);
            await loadScript(theme.scripts.shippingCalculator);
          }
          this.scriptsLoaded = true;
        }
      } catch (error) {
        console.log(error);
        // Still try to paint section HTML even if optional scripts fail.
        this.scriptsLoaded = true;
      }

      if (data && data.sections) {
        this.getSectionsToRender().forEach((section) => {
          const el = document.getElementById(section.id);
          if (!el) return;
          // Prefer exact section key; also try fuzzy match if Shopify renames keys.
          let sectionHtml = data.sections[section.section];
          if (!sectionHtml) {
            const altKey = Object.keys(data.sections).find(
              (k) => k === section.section || k.endsWith(section.section) || section.section.endsWith(k)
            );
            sectionHtml = altKey ? data.sections[altKey] : null;
          }
          const html = CartDrawer.getElementHTML(sectionHtml, section.selector);
          // Never wipe existing markup when the section payload is missing/unusable.
          if (!html) return;
          el.innerHTML = html;
        });
        // Re-bind nodes replaced via innerHTML.
        this.cartSummary = this.querySelector(".cart-drawer__summary");
      }

      if (openDrawer && this.getAttribute("open") === null) {
        // Defer so callers can await renderContents before open's sync runs.
        setTimeout(() => this.open(), 0);
      }
      if (window.initLazyImages) window.initLazyImages();
    }

    getSectionsToRender() {
      return [
        {
          id: "cart-drawer",
          section: this.closest(".shopify-section").id.replace("shopify-section-", ""),
          selector: "cart-drawer"
        },
        {
          id: "cart-icon-bubble",
          section: "cart-icon-bubble",
          selector: ".shopify-section"
        }
      ];
    }

    static recalculateCssVarHeights() {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent("on:css-var-height:update"));
      });
    }

    static getElementHTML(html, selector) {
      if (!html) return "";
      const tmpl = document.createElement("template");
      tmpl.innerHTML = html;
      const el = tmpl.content.querySelector(selector);
      return el ? el.innerHTML : "";
    }
  }

  customElements.define("cart-drawer", CartDrawer);
}
